import classes from "~/domains/product/components/PackagingSelection/NewPackagingSelection/v2/NewPackagingSelectionItem.module.less";
import type { PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { Card } from "~/features/configurator/Linear/components/Card";
import { type ReactNode, useMemo } from "react";
import { Box, Divider, FormControlLabel, Radio, Stack, Typography } from "~/common/mui";
import { getStockLevel, StockIndicatorIcon } from "~/domains/product/components/StockIndicator/StockIndicator";
import { CountryOfOriginDescriptorParam } from "~/domains/product/components/ProductParams";
import { useTranslation } from "react-i18next";
import { getPackagingLabel } from "~/features/cart/cart/utils/cartUtils";
import dayjs from "dayjs";
import { useIsPackagePhasedOut } from "~/features/productDetails/components/PhaseInOut/usePhaseInOutLogic";
import { DiscontinuationNotification } from "~/features/productDetails/components/PhaseInOut/DiscontinuationNotification/DiscontinuationNotification";
import { type PackagingSelectionVariant } from "~/domains/product/components/PackagingSelection/PackagingSelectionVariant";
import classNames from "classnames";
import { NumberFormatter } from "~/common/utils/NumberFormatter";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { C_OUTLINE, C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";

interface Props {
  className?: string;
  pack: PackagingInformationEntry;
  productDescriptor: ProductDescriptor;
  selected?: boolean;
  onSelect?: () => void;
  variant: PackagingSelectionVariant;
  disabled?: boolean;
}

export const NewPackagingSelectionItem = ({ selected, onSelect, productDescriptor, pack, disabled }: Props) => {
  const { t } = useTranslation();

  const packagingLabel = getPackagingLabel({
    t,
    productDescriptor,
    packSize: pack.packSize,
    packCode: pack.packCode,
  });

  const showDiscontinuedWarning = useIsPackagePhasedOut(productDescriptor, pack.packCode);
  const stockCalculations = useInStockCalculations(pack);

  const discontinuationNotification = showDiscontinuedWarning && (
    <DiscontinuationNotification
      className={classes.discontinuationNotification}
      discontinued={productDescriptor}
      variant={"package"}
    />
  );

  const isPackageOnSale = useSalesHelper(productDescriptor).isPackageOnSale(pack.packCode);

  const header = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <FormControlLabel
        key={pack.packCode}
        value={pack.packCode}
        checked={selected}
        control={<Radio sx={{ my: "-8px" }} color="onSecondaryContainer" />}
        label={<Typography variant="titleMedium">{packagingLabel}</Typography>}
        onChange={onSelect}
        disabled={disabled}
      />
      {isPackageOnSale && <SaleTag size={16} />}
    </Box>
  );

  const stockContent = (
    <Box className={classes.stockContent}>
      <StockIndicatorValue totalInStock={stockCalculations.totalInStock} />
      <InProductionSection pack={pack} />
      <CountryOfOriginDescriptorParam
        showEmpty
        descriptor={productDescriptor}
        packCode={pack.packCode}
        withoutLabel={true}
      />
    </Box>
  );

  const handleClickOnPackage = () => {
    if (onSelect) {
      onSelect();
    }
  };

  return (
    <Card className={classes.root} active={selected} onClick={handleClickOnPackage}>
      <CardContent className={classNames(classes.packageCard, selected && classes.packSelected)}>
        {discontinuationNotification}
        <Stack divider={<Divider sx={{ color: selected ? C_OUTLINE : C_OUTLINE_VARIANT }} />} gap={1}>
          {header}
          {stockContent}
        </Stack>
      </CardContent>
    </Card>
  );
};

const getCompactNumber = (value: number) => {
  const absValue = Math.abs(value);

  // If a number has more than 5 digits, round it up to the nearest thousand
  if (absValue < 10000) {
    return NumberFormatter.getDefaultFormatter().format(value);
  } else {
    const rounded = Math.round(absValue / 1000);
    return absValue > rounded * 1000 ? `${rounded}k+` : `${rounded}k`;
  }
};

const useInStockCalculations = (pack: PackagingInformationEntry) => {
  const { t } = useTranslation();
  return useMemo(() => {
    const readyForShipment = pack.totalWarehouse.inStock.find((entry) => !entry.date)?.quantity ?? 0;
    const inStockEntries = pack.totalWarehouse.inStock.map(
      (entry) =>
        [
          entry.quantity,
          entry.date
            ? dayjs(entry.date).format("DD.MM.YYYY")
            : t("productDetailsPage.availabilityTable.readyForShipment", { quantity: "" }),
        ] as const,
    );
    const totalInStock = inStockEntries.reduce((acc, [quantity]) => acc + quantity, 0);
    return { readyForShipment, inStockEntries, totalInStock };
  }, [pack]);
};

const StockIndicatorValue = ({ totalInStock, className }: { totalInStock: number; className?: string }) => {
  const { t } = useTranslation();

  return (
    <Stack direction={"row"} gap={1} className={classNames("d-flex flex-align-center", className)}>
      <StockIndicatorIcon className={classes.stockIcon} level={getStockLevel(totalInStock)} />
      <p>
        {totalInStock > 0 && t("stockIndicator.existsWithQty", { amount: getCompactNumber(totalInStock) })}
        {totalInStock === 0 && t("addToCartPopup.stock.info")}
      </p>
    </Stack>
  );
};

const InProductionSection = ({ pack }: { pack: PackagingInformationEntry }) => {
  const { t } = useTranslation();
  const isTablet = useIsMinTabletSize();

  const inProductionEntries = pack.totalWarehouse.inProduction.map(
    (entry) => [entry.quantity, dayjs(entry.date).format("DD.MM.YYYY")] as const,
  );

  if (pack.totalWarehouse.inClarification > 0) {
    inProductionEntries.unshift([
      pack.totalWarehouse.inClarification,
      t("product.availability.stock.table.inClarification"),
    ] as const);
  }
  const totalInProduction = inProductionEntries.reduce((acc, [quantity]) => acc + quantity, 0);
  return (
    <>
      <Stack
        direction={"row"}
        gap={1}
        className={"d-flex flex-align-center"}
        sx={{ visibility: totalInProduction <= 0 ? "hidden" : "visible" }}
      >
        <CalendarTodayOutlinedIcon fontSize="small" />
        {!isTablet ? (
          <Typography>{getCompactNumber(totalInProduction)}</Typography>
        ) : (
          <Typography>
            {t("productDetailsPage.availabilityTable.toCome", {
              quantity: getCompactNumber(totalInProduction),
            })}
          </Typography>
        )}
      </Stack>
    </>
  );
};

const CardContent = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={classNames(className, classes.cardContent)}>{children}</div>;
};
